/**
 * GeofenceExceptionsModule — admin Geofence Exceptions & Overrides
 * (ODE-P07-T07).
 *
 * Renders the read-only admin geofence surfaces with the full REQ-090 state
 * set:
 *
 *   Exceptions list (REQ-049/067) — the four non-override geofence decision
 *     states filtered by DO / checkpoint (CP3|CP6) / driver / vehicle / date
 *     range with opaque-cursor "muat lagi"; each row carries the CONSULT-TIME
 *     override state of its (DO, checkpoint) pair.
 *   Detail — the exception joined to its override provenance (actor, reason,
 *     created/revoked, REQ-076 audit reference).
 *   Overrides list — the ODE-P05-T04 records with the consult-time effective
 *     state (ACTIVE / EXPIRED / REVOKED) and provenance.
 *
 * Override create/revoke actions are POSTed to the VERIFIED ODE-P05-T04 write
 * routes (supervisor-only `geofence.override`, mandatory `x-purpose-code`) —
 * this module never writes the store directly (REQ-067/077). The action buttons
 * are hidden for non-authorized roles (UX mirror); the server guard remains the
 * authority.
 *
 * Consult-time EXPIRED is never computed here and never persisted: `state`,
 * `storedState` and `overridden` come from the server projection verbatim.
 * Status is never encoded by colour alone.
 *
 * Traces to: ODE-PRD-REQ-049, ODE-PRD-REQ-067, ODE-PRD-REQ-076,
 *            ODE-PRD-REQ-077, ODE-PRD-REQ-090.
 */

import { useState } from 'react';
import { useAuth } from '../../auth/AuthContext.js';
import type {
  AdminGeofenceExceptionDetail,
  AdminGeofenceExceptionRow,
  AdminGeofenceOverrideRow,
  GeofenceCheckpointId,
  GeofenceOverrideConsultState,
} from '../../api/client.js';
import { StateView } from '../../components/StateView.js';
import { hasStaffPermission } from '../../rbac/catalog.js';
import type { ModuleState } from '../state.js';
import {
  useGeofenceExceptions,
  type GeofenceExceptionsSeams,
  type GeofenceExceptionListState,
  type GeofenceOverrideListState,
} from './useGeofenceExceptions.js';

export type { GeofenceExceptionsSeams };

/** Plain-Indonesian labels for the four exception decisions (DND §8). */
const DECISION_LABELS: Readonly<Record<string, string>> = {
  OUTSIDE_GEOFENCE: 'Di luar geofence',
  LOCATION_ACCURACY_INSUFFICIENT: 'Akurasi lokasi tidak memadai',
  LOCATION_UNAVAILABLE: 'Lokasi tidak tersedia',
  VALIDATION_PENDING: 'Menunggu validasi',
};

/** Plain-Indonesian labels for the consult-time override states. */
const OVERRIDE_STATE_LABELS: Readonly<Record<GeofenceOverrideConsultState, string>> = {
  ACTIVE: 'Aktif',
  EXPIRED: 'Kedaluwarsa (saat konsultasi)',
  REVOKED: 'Dicabut',
};

const PURPOSE_CODE_PLACEHOLDER = 'mis. purpose-supervisor-override';

export function GeofenceExceptionsModule({
  seams = {},
}: {
  readonly seams?: GeofenceExceptionsSeams;
} = {}): JSX.Element {
  const { session } = useAuth();
  const roles = session?.staff.roles ?? [];
  const canWrite = hasStaffPermission(roles, 'geofence.override');
  const geo = useGeofenceExceptions({ token: session?.token ?? null, seams });
  const { form, setForm, state } = geo;

  return (
    <section aria-labelledby="geofence-exceptions-title" className="module">
      <h2 id="geofence-exceptions-title">Pengecualian &amp; Override Geofence</h2>
      <p className="module-description">
        Lihat pengecualian geofence (keputusan di luar/belum valid) dan catatan
        override supervisor beserta status efektif saat konsultasi (REQ-049/067/076).
      </p>

      <form
        className="do-search-form"
        data-testid="geofence-exceptions-form"
        onSubmit={(event) => {
          event.preventDefault();
          geo.submit();
        }}
      >
        <label>
          Nomor DO
          <input
            data-testid="filter-do-number"
            value={form.doNumber}
            onChange={(event) => setForm({ doNumber: event.target.value })}
            placeholder="mis. DO-7001"
          />
        </label>
        <label>
          ID Delivery Order
          <input
            data-testid="filter-delivery-order-id"
            value={form.deliveryOrderId}
            onChange={(event) => setForm({ deliveryOrderId: event.target.value })}
            placeholder="UUID"
          />
        </label>
        <label>
          Checkpoint
          <select
            data-testid="filter-checkpoint"
            value={form.checkpointId}
            onChange={(event) => setForm({ checkpointId: event.target.value })}
          >
            <option value="">Semua checkpoint</option>
            <option value="CP3">CP3</option>
            <option value="CP6">CP6</option>
          </select>
        </label>
        <label>
          Driver
          <input
            data-testid="filter-driver"
            value={form.driverRef}
            onChange={(event) => setForm({ driverRef: event.target.value })}
          />
        </label>
        <label>
          Kendaraan
          <input
            data-testid="filter-vehicle"
            value={form.vehicleRef}
            onChange={(event) => setForm({ vehicleRef: event.target.value })}
          />
        </label>
        <label>
          Dari tanggal
          <input
            data-testid="filter-from"
            value={form.from}
            onChange={(event) => setForm({ from: event.target.value })}
            placeholder="YYYY-MM-DD"
          />
        </label>
        <label>
          Sampai tanggal
          <input
            data-testid="filter-to"
            value={form.to}
            onChange={(event) => setForm({ to: event.target.value })}
            placeholder="YYYY-MM-DD"
          />
        </label>
        <label>
          Batas baris
          <input
            data-testid="filter-limit"
            value={form.limit}
            onChange={(event) => setForm({ limit: event.target.value })}
            placeholder="20"
          />
        </label>
        <div className="do-search-actions">
          <button type="submit" data-testid="geofence-exceptions-submit">
            Cari
          </button>
          <button type="button" data-testid="geofence-exceptions-reset" onClick={geo.reset}>
            Atur ulang
          </button>
        </div>
      </form>

      {state.kind === 'pending' ? (
        <StateView state={{ kind: 'pending' }} />
      ) : state.kind === 'ready' ? (
        <ExceptionResults state={state} onOpenDetail={geo.openDetail} onLoadMore={geo.loadMore} />
      ) : (
        <StateView state={toModuleState(state)} onRetry={geo.submit} />
      )}

      <DetailPanel
        state={geo.detailState}
        canWrite={canWrite}
        actionState={geo.actionState}
        onClose={geo.closeDetail}
        onCreate={geo.createOverride}
        onRevoke={geo.revokeOverride}
        onClearAction={geo.clearAction}
      />

      <OverridesSection
        state={geo.overridesState}
        filter={geo.overrideFilter}
        canWrite={canWrite}
        onFilterChange={geo.setOverrideFilter}
        onLoad={geo.loadOverrides}
        onRevoke={geo.revokeOverride}
      />
    </section>
  );
}

/** Maps a list state to the shared REQ-090 renderer. */
function toModuleState(state: GeofenceExceptionListState): ModuleState {
  switch (state.kind) {
    case 'pending':
      return { kind: 'pending' };
    case 'loading':
      return { kind: 'loading' };
    case 'ready':
      return { kind: 'empty', payload: state.page };
    case 'unauthorized':
      return { kind: 'unauthorized' };
    case 'blocked':
      return { kind: 'blocked', message: state.message };
    case 'error':
      return { kind: 'error', message: state.message };
  }
}

function OverrideStateBadge({ state }: { readonly state: GeofenceOverrideConsultState }): JSX.Element {
  return (
    <span className={`badge badge-${state.toLowerCase()}`} data-testid={`override-state-${state}`}>
      {OVERRIDE_STATE_LABELS[state]}
    </span>
  );
}

function ExceptionResults({
  state,
  onOpenDetail,
  onLoadMore,
}: {
  readonly state: Extract<GeofenceExceptionListState, { kind: 'ready' }>;
  readonly onOpenDetail: (exceptionId: string) => void;
  readonly onLoadMore: () => void;
}): JSX.Element {
  const { page } = state;
  if (page.rows.length === 0) {
    return (
      <div data-testid="geofence-exceptions-empty" className="state state-empty">
        <h3>Belum ada data</h3>
        <p>Tidak ada pengecualian geofence yang cocok dengan filter ini.</p>
      </div>
    );
  }
  return (
    <div data-testid="geofence-exceptions-results">
      <table className="overview-table">
        <thead>
          <tr>
            <th scope="col">Nomor DO</th>
            <th scope="col">Checkpoint</th>
            <th scope="col">Keputusan</th>
            <th scope="col">Jarak (m)</th>
            <th scope="col">Akurasi (m)</th>
            <th scope="col">Waktu</th>
            <th scope="col">Override</th>
            <th scope="col">Aksi</th>
          </tr>
        </thead>
        <tbody>
          {page.rows.map((row) => (
            <ExceptionRowView key={row.exceptionId} row={row} onOpenDetail={onOpenDetail} />
          ))}
        </tbody>
      </table>
      <p className="do-search-asof" data-testid="geofence-exceptions-asof">
        {page.rows.length} baris · per {new Date(page.asOfEpochMs).toISOString()}
      </p>
      {page.hasMore && (
        <button
          type="button"
          data-testid="geofence-exceptions-load-more"
          onClick={onLoadMore}
          disabled={state.loadingMore}
        >
          {state.loadingMore ? 'Memuat…' : 'Muat lagi'}
        </button>
      )}
    </div>
  );
}

function ExceptionRowView({
  row,
  onOpenDetail,
}: {
  readonly row: AdminGeofenceExceptionRow;
  readonly onOpenDetail: (exceptionId: string) => void;
}): JSX.Element {
  return (
    <tr data-testid={`geofence-exception-${row.exceptionId}`}>
      <td>{row.doNumber}</td>
      <td data-testid={`exception-checkpoint-${row.exceptionId}`}>{row.checkpointId}</td>
      <td data-testid={`exception-decision-${row.exceptionId}`}>
        {DECISION_LABELS[row.decision] ?? row.decision}
      </td>
      <td>{row.distanceMeters ?? '—'}</td>
      <td>{row.accuracyMeters ?? '—'}</td>
      <td>{row.occurredAt}</td>
      <td data-testid={`exception-override-${row.exceptionId}`}>
        {row.overrideState === null ? '—' : <OverrideStateBadge state={row.overrideState} />}
      </td>
      <td>
        <button
          type="button"
          data-testid={`exception-detail-${row.exceptionId}`}
          onClick={() => onOpenDetail(row.exceptionId)}
        >
          Detail
        </button>
      </td>
    </tr>
  );
}

function DetailPanel({
  state,
  canWrite,
  actionState,
  onClose,
  onCreate,
  onRevoke,
  onClearAction,
}: {
  readonly state: ReturnType<typeof useGeofenceExceptions>['detailState'];
  readonly canWrite: boolean;
  readonly actionState: ReturnType<typeof useGeofenceExceptions>['actionState'];
  readonly onClose: () => void;
  readonly onCreate: ReturnType<typeof useGeofenceExceptions>['createOverride'];
  readonly onRevoke: ReturnType<typeof useGeofenceExceptions>['revokeOverride'];
  readonly onClearAction: () => void;
}): JSX.Element | null {
  if (state.kind === 'idle') return null;

  return (
    <section aria-label="Detail pengecualian geofence" data-testid="geofence-exception-detail" className="do-detail">
      <div className="do-detail-head">
        <h3>Detail Pengecualian</h3>
        <button type="button" data-testid="geofence-exception-detail-close" onClick={onClose}>
          Tutup
        </button>
      </div>
      {state.kind === 'loading' && <StateView state={{ kind: 'loading' }} />}
      {state.kind === 'not_found' && (
        <div role="alert" data-testid="geofence-exception-detail-not-found" className="state state-error">
          <h3>Tidak ditemukan</h3>
          <p>Pengecualian geofence tidak dikenal.</p>
        </div>
      )}
      {state.kind === 'unauthorized' && <StateView state={{ kind: 'unauthorized' }} />}
      {state.kind === 'blocked' && <StateView state={{ kind: 'blocked', message: state.message }} />}
      {state.kind === 'error' && <StateView state={{ kind: 'error', message: state.message }} />}
      {state.kind === 'ready' && (
        <DetailBody
          detail={state.detail}
          canWrite={canWrite}
          actionState={actionState}
          onCreate={onCreate}
          onRevoke={onRevoke}
          onClearAction={onClearAction}
        />
      )}
    </section>
  );
}

function DetailBody({
  detail,
  canWrite,
  actionState,
  onCreate,
  onRevoke,
  onClearAction,
}: {
  readonly detail: AdminGeofenceExceptionDetail;
  readonly canWrite: boolean;
  readonly actionState: ReturnType<typeof useGeofenceExceptions>['actionState'];
  readonly onCreate: ReturnType<typeof useGeofenceExceptions>['createOverride'];
  readonly onRevoke: ReturnType<typeof useGeofenceExceptions>['revokeOverride'];
  readonly onClearAction: () => void;
}): JSX.Element {
  const exception = detail.exception;
  const override = detail.override;
  const [reason, setReason] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [purposeCode, setPurposeCode] = useState('');

  return (
    <div data-testid="geofence-exception-detail-ready">
      <dl className="do-detail-grid">
        <dt>Nomor DO</dt>
        <dd data-testid="detail-do-number">{exception.doNumber}</dd>
        <dt>Checkpoint</dt>
        <dd data-testid="detail-checkpoint">{exception.checkpointId}</dd>
        <dt>Keputusan</dt>
        <dd data-testid="detail-decision">{DECISION_LABELS[exception.decision] ?? exception.decision}</dd>
        <dt>Jarak (m)</dt>
        <dd>{exception.distanceMeters ?? '—'}</dd>
        <dt>Akurasi (m)</dt>
        <dd>{exception.accuracyMeters ?? '—'}</dd>
        <dt>Waktu</dt>
        <dd>{exception.occurredAt}</dd>
      </dl>

      <section aria-label="Provenance override" data-testid="detail-override">
        <h4>Override (REQ-067/076)</h4>
        {override === null ? (
          <p data-testid="detail-override-none">Tidak ada override untuk pasangan (DO, checkpoint) ini.</p>
        ) : (
          <dl className="do-detail-grid">
            <dt>Status konsultasi</dt>
            <dd data-testid="detail-override-state">
              <OverrideStateBadge state={override.state} /> (tersimpan: {override.storedState})
            </dd>
            <dt>Alasan</dt>
            <dd data-testid="detail-override-reason">{override.reason}</dd>
            <dt>Aktor</dt>
            <dd data-testid="detail-override-actor">
              {override.actorType} · {override.actorId}
            </dd>
            <dt>Dibuat</dt>
            <dd>{override.createdAt}</dd>
            {override.revokedAt !== null && (
              <>
                <dt>Dicabut</dt>
                <dd data-testid="detail-override-revoked">
                  {override.revokedAt} · {override.revokedBy ?? '—'} · {override.revokeReason ?? '—'}
                </dd>
              </>
            )}
            <dt>Ref audit</dt>
            <dd data-testid="detail-override-audit">{override.auditRef ?? '—'}</dd>
          </dl>
        )}
      </section>

      {canWrite ? (
        <section aria-label="Tindakan override" data-testid="override-actions">
          <h4>Tindakan supervisor (REQ-067)</h4>
          <p className="module-description">
            Tindakan ini dikirim ke endpoint override supervisor yang terverifikasi (P05-T04)
            dengan kode tujuan wajib dan audit REQ-076.
          </p>
          <label>
            Alasan
            <input
              data-testid="override-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </label>
          <label>
            Kedaluwarsa (opsional, ISO-8601)
            <input
              data-testid="override-expires-at"
              value={expiresAt}
              onChange={(event) => setExpiresAt(event.target.value)}
              placeholder="2026-10-12T00:00:00.000Z"
            />
          </label>
          <label>
            Kode tujuan
            <input
              data-testid="override-purpose"
              value={purposeCode}
              onChange={(event) => setPurposeCode(event.target.value)}
              placeholder={PURPOSE_CODE_PLACEHOLDER}
            />
          </label>
          <div className="do-search-actions">
            <button
              type="button"
              data-testid="override-create"
              onClick={() =>
                onCreate({
                  deliveryOrderId: exception.deliveryOrderId,
                  checkpointId: exception.checkpointId as GeofenceCheckpointId,
                  reason,
                  expiresAt,
                  purposeCode,
                })
              }
            >
              Buat override
            </button>
            <button
              type="button"
              data-testid="override-revoke"
              onClick={() =>
                onRevoke({
                  deliveryOrderId: exception.deliveryOrderId,
                  checkpointId: exception.checkpointId as GeofenceCheckpointId,
                  reason,
                  purposeCode,
                })
              }
            >
              Cabut override
            </button>
          </div>
          <ActionStateView state={actionState} onClear={onClearAction} />
        </section>
      ) : (
        <p data-testid="override-actions-hidden" className="module-description">
          Hanya supervisor yang dapat membuat/mencabut override.
        </p>
      )}
    </div>
  );
}

function ActionStateView({
  state,
  onClear,
}: {
  readonly state: ReturnType<typeof useGeofenceExceptions>['actionState'];
  readonly onClear: () => void;
}): JSX.Element | null {
  if (state.kind === 'idle') return null;
  if (state.kind === 'submitting') {
    return <StateView state={{ kind: 'loading' }} />;
  }
  if (state.kind === 'succeeded') {
    return (
      <div role="status" data-testid="override-action-success" className="state state-empty">
        <h3>Berhasil</h3>
        <p>
          {state.message} ({state.outcome})
        </p>
        <button type="button" onClick={onClear}>
          Tutup
        </button>
      </div>
    );
  }
  if (state.kind === 'unauthorized') return <StateView state={{ kind: 'unauthorized' }} />;
  if (state.kind === 'blocked') return <StateView state={{ kind: 'blocked', message: state.message }} />;
  return <StateView state={{ kind: 'error', message: state.message }} />;
}

function OverridesSection({
  state,
  filter,
  canWrite,
  onFilterChange,
  onLoad,
  onRevoke,
}: {
  readonly state: GeofenceOverrideListState;
  readonly filter: GeofenceOverrideConsultState | '';
  readonly canWrite: boolean;
  readonly onFilterChange: (value: GeofenceOverrideConsultState | '') => void;
  readonly onLoad: () => void;
  readonly onRevoke: ReturnType<typeof useGeofenceExceptions>['revokeOverride'];
}): JSX.Element {
  return (
    <section aria-label="Daftar override" data-testid="geofence-overrides-section" className="do-detail">
      <div className="do-detail-head">
        <h3>Override Supervisor</h3>
      </div>
      <label>
        Status konsultasi
        <select
          data-testid="overrides-filter-state"
          value={filter}
          onChange={(event) => onFilterChange(event.target.value as GeofenceOverrideConsultState | '')}
        >
          <option value="">Semua status</option>
          <option value="ACTIVE">Aktif</option>
          <option value="EXPIRED">Kedaluwarsa (saat konsultasi)</option>
          <option value="REVOKED">Dicabut</option>
        </select>
      </label>
      <div className="do-search-actions">
        <button type="button" data-testid="overrides-load" onClick={onLoad}>
          Muat override
        </button>
      </div>

      {state.kind === 'pending' && <StateView state={{ kind: 'pending' }} />}
      {state.kind === 'loading' && <StateView state={{ kind: 'loading' }} />}
      {state.kind === 'unauthorized' && <StateView state={{ kind: 'unauthorized' }} />}
      {state.kind === 'blocked' && <StateView state={{ kind: 'blocked', message: state.message }} />}
      {state.kind === 'error' && <StateView state={{ kind: 'error', message: state.message }} />}
      {state.kind === 'ready' &&
        (state.page.rows.length === 0 ? (
          <div data-testid="geofence-overrides-empty" className="state state-empty">
            <h3>Belum ada data</h3>
            <p>Tidak ada override untuk filter ini.</p>
          </div>
        ) : (
          <table className="overview-table" data-testid="geofence-overrides-results">
            <thead>
              <tr>
                <th scope="col">Delivery Order</th>
                <th scope="col">Checkpoint</th>
                <th scope="col">Status</th>
                <th scope="col">Aktor</th>
                <th scope="col">Alasan</th>
                <th scope="col">Ref audit</th>
                {canWrite && <th scope="col">Aksi</th>}
              </tr>
            </thead>
            <tbody>
              {state.page.rows.map((row) => (
                <OverrideRowView key={`${row.deliveryOrderId}:${row.checkpointId}`} row={row} canWrite={canWrite} onRevoke={onRevoke} />
              ))}
            </tbody>
          </table>
        ))}
    </section>
  );
}

function OverrideRowView({
  row,
  canWrite,
  onRevoke,
}: {
  readonly row: AdminGeofenceOverrideRow;
  readonly canWrite: boolean;
  readonly onRevoke: ReturnType<typeof useGeofenceExceptions>['revokeOverride'];
}): JSX.Element {
  const [reason, setReason] = useState('');
  const [purposeCode, setPurposeCode] = useState('');
  return (
    <tr data-testid={`override-row-${row.deliveryOrderId}-${row.checkpointId}`}>
      <td>{row.deliveryOrderId}</td>
      <td>{row.checkpointId}</td>
      <td data-testid={`override-state-${row.deliveryOrderId}-${row.checkpointId}`}>
        <OverrideStateBadge state={row.state} />
      </td>
      <td>
        {row.actorType} · {row.actorId}
      </td>
      <td>{row.reason}</td>
      <td>{row.auditRef ?? '—'}</td>
      {canWrite && (
        <td>
          <input
            data-testid={`override-revoke-reason-${row.deliveryOrderId}-${row.checkpointId}`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="alasan cabut"
          />
          <input
            data-testid={`override-revoke-purpose-${row.deliveryOrderId}-${row.checkpointId}`}
            value={purposeCode}
            onChange={(event) => setPurposeCode(event.target.value)}
            placeholder="kode tujuan"
          />
          <button
            type="button"
            data-testid={`override-revoke-${row.deliveryOrderId}-${row.checkpointId}`}
            onClick={() =>
              onRevoke({
                deliveryOrderId: row.deliveryOrderId,
                checkpointId: row.checkpointId as GeofenceCheckpointId,
                reason,
                purposeCode,
              })
            }
          >
            Cabut
          </button>
        </td>
      )}
    </tr>
  );
}
